(() => {
  if (window.CAFThemeHotfixShared) return;

  const PDP_LAYOUT_STORAGE_KEY = 'caf_pdp_layout_mode';
  const DEFAULT_PDP_LAYOUT_MODE = '3';
  const PRODUCT_DETAILS_SELECTOR = '.product__details--product-page';
  const JDGM_PREVIEW_BADGE_SELECTOR = '.jdgm-preview-badge[data-id]';
  const JUDGE_ME_REVIEW_WIDGET_SELECTORS = [
    '#judgeme_product_reviews',
    '#judgeme_all_reviews_page',
    '.jdgm-all-reviews-page',
    '.jdgm-all-reviews-widget',
    '.jdgm-all-reviews-widget-v2025',
    '.jdgm-widget-revamp',
  ];
  const JUDGE_ME_REVIEW_WIDGET_SELECTOR = JUDGE_ME_REVIEW_WIDGET_SELECTORS.join(', ');
  const JUDGE_ME_REVIEW_IMAGE_SELECTOR = JUDGE_ME_REVIEW_WIDGET_SELECTORS.map((selector) => `${selector} img`).join(', ');
  const JUDGE_ME_REVIEW_IMAGE_SHELL_SELECTOR = [
    '.jdgm-rev__pic-link',
    '.jdgm-rev__pic',
    '.jdgm-rev__picture-link',
    '.jdgm-gallery__image-wrapper',
    '.jdgm-review-card__image',
    '.jdgm-review-card__media-item',
    '.jdgm-media-fieldset__medium',
  ].join(', ');
  const JUDGE_ME_REVIEW_IMAGE_GROUP_SELECTOR = [
    '.jdgm-rev__pics',
    '.jdgm-review-card__media',
    '.jdgm-review-card__pictures',
    '.jdgm-gallery',
    '.jdgm-medias',
  ].join(', ');
  const TEEINBLUE_EVENT_COMPONENT_INJECTED = 'teeinblue-event-component-injected';
  const JUDGE_ME_LOAD_EVENTS = ['jdgm:loaded', 'judgeme:loaded'];
  const POPUP_MODAL_SURFACE_SELECTOR = '.vm--modal, .vm--container, .tee-dialog';
  const DISCOUNT_PRICE_APP_SELECTOR = '.product-block--discount-price-app';
  const CART_PREVIEW_CHECKOUT_SELECTOR = '.tee-preview-button__checkout';
  const CART_PREVIEW_MUTATION_SELECTOR = `${CART_PREVIEW_CHECKOUT_SELECTOR}, ${POPUP_MODAL_SURFACE_SELECTOR}`;
  const MOBILE_POPUP_BREAKPOINT = 768;

  const hasBodyClassMatch = (pattern) => pattern.test(document.body?.className || '');
  const pathIncludes = (segment) => window.location.pathname.includes(segment);
  const isPathCart = () => window.location.pathname === '/cart' || window.location.pathname.startsWith('/cart');
  const isProductContext = (fallbackSelector) => {
    if (hasBodyClassMatch(/template-product|product/)) return true;
    if (pathIncludes('/products/')) return true;
    return fallbackSelector ? Boolean(document.querySelector(fallbackSelector)) : false;
  };
  const hasFacetedFilterUi = () => {
    return Boolean(document.querySelector('[data-faceted-filter-form], .faceted-filters, .faceted-filter-group'));
  };
  const isCollectionOrSearchContext = () => {
    return hasBodyClassMatch(/template-(collection|search)/) || pathIncludes('/collections/') || pathIncludes('/search');
  };
  const isCartContext = () => {
    return hasBodyClassMatch(/\btemplate-cart\b|\bcart\b/) || isPathCart();
  };
  const runWithDelays = (fn, delays) => {
    delays.forEach((delay) => {
      window.setTimeout(fn, delay);
    });
  };
  const createRafThrottled = (fn, guard = () => false) => {
    let pending = false;

    return (...args) => {
      if (pending || guard()) return;
      pending = true;
      window.requestAnimationFrame(() => {
        pending = false;
        if (guard()) return;
        fn(...args);
      });
    };
  };
  const nodeMatchesOrContains = (node, selector) => {
    if (!(node instanceof Element)) return false;
    if (node.matches(selector)) return true;
    return Boolean(node.querySelector(selector));
  };
  const mutationsContainAddedNode = (mutations, selector) => {
    return mutations.some((mutation) => {
      return Array.from(mutation.addedNodes || []).some((node) => nodeMatchesOrContains(node, selector));
    });
  };
  const mutationsHaveAddedNodes = (mutations) => {
    return mutations.some((mutation) => (mutation.addedNodes?.length || 0) > 0);
  };
  const bindJudgeMeAndTeeinblueEvents = (handler) => {
    document.addEventListener(TEEINBLUE_EVENT_COMPONENT_INJECTED, handler);
    JUDGE_ME_LOAD_EVENTS.forEach((eventName) => {
      document.addEventListener(eventName, handler);
    });
  };
  const clearInlineContextMenuLocks = () => {
    [document, document.documentElement, document.body].forEach((node) => {
      if (!node) return;
      if (typeof node.oncontextmenu === 'function') {
        node.oncontextmenu = null;
      }
    });

    document.querySelectorAll('[oncontextmenu]').forEach((node) => {
      node.removeAttribute('oncontextmenu');
      if (typeof node.oncontextmenu === 'function') {
        node.oncontextmenu = null;
      }
    });
  };
  const allowNativeContextMenu = () => {
    if (window.__cafNativeContextMenuAllowed) return;
    window.__cafNativeContextMenuAllowed = true;

    const clearLocks = () => clearInlineContextMenuLocks();

    window.addEventListener(
      'contextmenu',
      (event) => {
        clearLocks();
        event.stopImmediatePropagation();
      },
      true,
    );

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', clearLocks, { once: true });
    } else {
      clearLocks();
    }

    runWithDelays(clearLocks, [120, 600, 1500, 3600]);
  };
  const removeClassFromAll = (scope, selector, className) => {
    scope.querySelectorAll(selector).forEach((node) => {
      node.classList.remove(className);
    });
  };
  const removeAll = (scope, selector) => {
    scope.querySelectorAll(selector).forEach((node) => {
      node.remove();
    });
  };
  const unwrapAll = (scope, selector) => {
    scope.querySelectorAll(selector).forEach((wrapper) => {
      if (wrapper.parentElement) {
        while (wrapper.firstChild) {
          wrapper.parentElement.insertBefore(wrapper.firstChild, wrapper);
        }
      }
      wrapper.remove();
    });
  };
  const setImportantStyles = (node, styles) => {
    if (!(node instanceof HTMLElement)) return;
    Object.entries(styles).forEach(([property, value]) => {
      node.style.setProperty(property, value, 'important');
    });
  };
  const isJudgeMeReviewContext = () => {
    return (
      pathIncludes('/pages/review') ||
      pathIncludes('/pages/reviews') ||
      isProductContext('#judgeme_product_reviews') ||
      Boolean(document.querySelector(JUDGE_ME_REVIEW_WIDGET_SELECTOR))
    );
  };
  const hasHealthyJudgeMeReviewImage = (image) => {
    if (!(image instanceof HTMLImageElement)) return false;
    if (image.dataset.cafJdgmBrokenImage === 'true') return false;
    return !image.complete || image.naturalWidth > 0;
  };
  const refreshJudgeMeReviewImageGroup = (group) => {
    if (!(group instanceof Element)) return;
    const hasHealthyImage = Array.from(group.querySelectorAll('img')).some(hasHealthyJudgeMeReviewImage);
    group.classList.toggle('caf-jdgm-review-media-empty', !hasHealthyImage);
  };
  const markBrokenJudgeMeReviewImage = (image) => {
    if (!(image instanceof HTMLImageElement)) return;
    if (!image.closest(JUDGE_ME_REVIEW_WIDGET_SELECTOR)) return;

    image.dataset.cafJdgmBrokenImage = 'true';
    image.classList.add('caf-jdgm-broken-review-image');

    const shell = image.closest(JUDGE_ME_REVIEW_IMAGE_SHELL_SELECTOR);
    if (shell) {
      shell.classList.add('caf-jdgm-broken-review-media');
    }

    refreshJudgeMeReviewImageGroup(image.closest(JUDGE_ME_REVIEW_IMAGE_GROUP_SELECTOR));
  };
  const markExistingBrokenJudgeMeReviewImages = (scope = document) => {
    if (!isJudgeMeReviewContext()) return;

    scope.querySelectorAll(JUDGE_ME_REVIEW_IMAGE_SELECTOR).forEach((image) => {
      if (image.complete && image.naturalWidth === 0) {
        markBrokenJudgeMeReviewImage(image);
      } else {
        refreshJudgeMeReviewImageGroup(image.closest(JUDGE_ME_REVIEW_IMAGE_GROUP_SELECTOR));
      }
    });
  };
  const startBrokenJudgeMeReviewImageGuard = () => {
    if (window.CAFJudgeMeBrokenReviewImageGuard) return;
    window.CAFJudgeMeBrokenReviewImageGuard = true;

    const scheduleScan = createRafThrottled(() => markExistingBrokenJudgeMeReviewImages());

    document.addEventListener(
      'error',
      (event) => {
        markBrokenJudgeMeReviewImage(event.target);
      },
      true,
    );

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', scheduleScan, { once: true });
    } else {
      scheduleScan();
    }

    bindJudgeMeAndTeeinblueEvents(scheduleScan);
    runWithDelays(scheduleScan, [300, 1200, 3000, 6000]);

    new MutationObserver((mutations) => {
      if (mutationsContainAddedNode(mutations, JUDGE_ME_REVIEW_IMAGE_SELECTOR)) {
        scheduleScan();
      }
    }).observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  };
  const guardBrokenJudgeMeReviewImages = () => {
    if (window.CAFJudgeMeBrokenReviewImageGuard || window.CAFJudgeMeBrokenReviewImageWatcher) return;

    if (isJudgeMeReviewContext()) {
      startBrokenJudgeMeReviewImageGuard();
      return;
    }

    window.CAFJudgeMeBrokenReviewImageWatcher = true;

    /*
      Performance guard: product cards and generic pages load shared hotfixes,
      but broken Judge.me review media only exists on PDP/reviews widgets.
      Keep a light one-shot watcher until a real widget appears instead of
      attaching global image-error listeners and delayed scans on every page.
    */
    let watcher = null;
    const startWhenJudgeMeAppears = createRafThrottled(() => {
      if (!isJudgeMeReviewContext()) return;
      startBrokenJudgeMeReviewImageGuard();
      if (watcher) {
        watcher.disconnect();
      }
    });

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', startWhenJudgeMeAppears, { once: true });
    } else {
      startWhenJudgeMeAppears();
    }

    if (!window.MutationObserver || !document.documentElement) {
      startBrokenJudgeMeReviewImageGuard();
      return;
    }

    watcher = new MutationObserver((mutations) => {
      if (window.CAFJudgeMeBrokenReviewImageGuard) {
        watcher.disconnect();
        return;
      }

      if (mutationsContainAddedNode(mutations, `${JUDGE_ME_REVIEW_WIDGET_SELECTOR}, ${JUDGE_ME_REVIEW_IMAGE_SELECTOR}`)) {
        startWhenJudgeMeAppears();
      }
    });

    watcher.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  };

  const normalizePdpLayoutMode = (value) => {
    const raw = String(value || '')
      .trim()
      .toLowerCase();

    const aliases = {
      1: '1',
      2: '2',
      3: '3',
      compact: '1',
      'compact-safe': '1',
      safe: '1',
      conversion: '2',
      'conversion-first': '2',
      urgency: '2',
      ultra: '3',
      'ultra-compact': '3',
      'ultra-compact-mobile': '3',
      mobile: '3',
    };

    return aliases[raw] || DEFAULT_PDP_LAYOUT_MODE;
  };

  const resolvePdpLayoutMode = () => {
    const params = new URLSearchParams(window.location.search);
    const fromQuery = params.get('pdp_mode') || params.get('pdp_layout');

    if (fromQuery) {
      const normalized = normalizePdpLayoutMode(fromQuery);
      try {
        window.localStorage.setItem(PDP_LAYOUT_STORAGE_KEY, normalized);
      } catch (error) {
        // Ignore storage failures in private browsing modes.
      }
      return normalized;
    }

    try {
      const fromStorage = window.localStorage.getItem(PDP_LAYOUT_STORAGE_KEY);
      if (fromStorage) {
        return normalizePdpLayoutMode(fromStorage);
      }
    } catch (error) {
      // Ignore storage failures and fall back to default.
    }

    return DEFAULT_PDP_LAYOUT_MODE;
  };

  const normalizeInlineText = (value) =>
    String(value || '')
      .replace(/\s+/g, ' ')
      .trim();

  const extractFirstInteger = (value) => {
    const match = String(value || '').match(/(\d[\d,.]*)/);
    if (!match) return null;
    const numeric = Number(match[1].replace(/[^\d]/g, ''));
    if (!Number.isFinite(numeric)) return null;
    return Math.max(0, Math.round(numeric));
  };

  const hashSeed = (value) => {
    let hash = 2166136261;
    const input = String(value || '');
    for (let index = 0; index < input.length; index += 1) {
      hash ^= input.charCodeAt(index);
      hash = Math.imul(hash, 16777619);
    }
    return hash >>> 0;
  };

  const seededIntegerInRange = (seed, min, max) => {
    const lower = Math.min(min, max);
    const upper = Math.max(min, max);
    const range = upper - lower + 1;
    return lower + (hashSeed(seed) % range);
  };

  const formatIntegerWithCommas = (value) => Number(value || 0).toLocaleString('en-US');
  const parseFirstDecimal = (value) => {
    const match = String(value || '').match(/(\d+(?:\.\d+)?)/);
    if (!match) return null;
    const numeric = Number.parseFloat(match[1]);
    return Number.isFinite(numeric) ? numeric : null;
  };
  const formatReviewRatingValue = (value) => {
    const numeric = parseFirstDecimal(value);
    const rating = numeric && numeric > 0 ? Math.min(5, Math.max(0, numeric)) : 5;
    return rating.toFixed(1);
  };
  const CAF_REVIEW_STAR_SVG =
    '<svg class="caf-icon caf-icon--rating-star" width="14" height="14" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 3l2.5 5.5 6 .5-4.5 4 1.5 6L12 16l-5.5 3 1.5-6-4.5-4 6-.5L12 3z" fill="currentColor"/></svg>';
  const getJudgeMePreviewRatingValue = (previewBadge) =>
    formatReviewRatingValue(
      previewBadge?.getAttribute('data-average-rating') ||
        previewBadge?.dataset.averageRating ||
        previewBadge?.querySelector('.jdgm-prev-badge__stars')?.getAttribute('data-score') ||
        previewBadge?.querySelector('[data-score]')?.getAttribute('data-score') ||
        previewBadge?.textContent ||
        '',
    );
  const getSyntheticJudgeMeDisplayRating = (productId) => {
    // Seeded per product so preview ratings look natural without changing on every page load.
    return (seededIntegerInRange(`caf-jdgm-display-rating:${productId}`, 48, 50) / 10).toFixed(1);
  };
  const formatReviewCountText = (count, sourceText, ratingValue = 5) => {
    const rating = formatReviewRatingValue(ratingValue);
    // CAF preview badges intentionally show one inline star icon with the numeric rating and count.
    return `<span class="caf-jdgm-inline-rating">${rating}</span> <span class="caf-jdgm-inline-star" aria-hidden="true">${CAF_REVIEW_STAR_SVG}</span> <span class="caf-jdgm-inline-count">(${formatIntegerWithCommas(count)})</span>`;
  };
  const ensureJudgeMePreviewBadgeShell = (widget) => {
    if (!widget) return null;

    let previewBadge = widget.querySelector('.jdgm-prev-badge');
    if (!previewBadge) {
      previewBadge = document.createElement('div');
      previewBadge.className = 'jdgm-prev-badge';
      previewBadge.setAttribute('data-average-rating', '5.00');
      previewBadge.setAttribute('data-number-of-reviews', '0');
      previewBadge.setAttribute('data-number-of-questions', '0');
      widget.appendChild(previewBadge);
      widget.dataset.cafJdgmFallbackShell = 'true';
    }

    let stars = previewBadge.querySelector('.jdgm-prev-badge__stars');
    if (!stars) {
      stars = document.createElement('span');
      stars.className = 'jdgm-prev-badge__stars';
      stars.setAttribute('data-score', '5.00');
      previewBadge.prepend(stars);
    }

    if (!stars.querySelector('.jdgm-star')) {
      for (let index = 0; index < 5; index += 1) {
        const star = document.createElement('span');
        star.className = 'jdgm-star jdgm--on';
        stars.appendChild(star);
      }
    }

    if (!previewBadge.querySelector('.jdgm-prev-badge__text')) {
      const text = document.createElement('span');
      text.className = 'jdgm-prev-badge__text';
      text.textContent = '0 reviews';
      previewBadge.appendChild(text);
    }

    return previewBadge;
  };
  const applySyntheticJudgeMePreviewCounts = (scope = document) => {
    if (!scope || typeof scope.querySelectorAll !== 'function') return;

    scope.querySelectorAll(JDGM_PREVIEW_BADGE_SELECTOR).forEach((widget) => {
      const productId = normalizeInlineText(widget.getAttribute('data-id'));
      if (!productId) return;

      const previewBadge = ensureJudgeMePreviewBadgeShell(widget);
      if (!previewBadge) return;

      let textNodes = Array.from(widget.querySelectorAll('.jdgm-prev-badge__text'));
      if (textNodes.length === 0) {
        const fallbackTextNode = document.createElement('span');
        fallbackTextNode.className = 'jdgm-prev-badge__text';
        fallbackTextNode.textContent = '0 reviews';
        previewBadge.appendChild(fallbackTextNode);
        textNodes = [fallbackTextNode];
      }

      const attrCount = extractFirstInteger(previewBadge.getAttribute('data-number-of-reviews'));
      const textCount = extractFirstInteger(textNodes[0]?.textContent);
      const domCount = Math.max(0, attrCount ?? textCount ?? 0);
      const hasStoredRealCount = widget.dataset.cafJdgmRealReviewCount !== undefined;
      const storedRealCount = Math.max(0, Number(widget.dataset.cafJdgmRealReviewCount || 0));
      const storedSyntheticCount = Math.max(0, Number(widget.dataset.cafJdgmSyntheticReviewCount || 0));
      const syntheticAlreadyApplied = previewBadge.dataset.cafSyntheticApplied === 'true';

      let shouldRefreshRealCount = !hasStoredRealCount || !syntheticAlreadyApplied;

      if (syntheticAlreadyApplied && hasStoredRealCount && domCount !== storedSyntheticCount) {
        shouldRefreshRealCount = true;
      }

      const realReviewCount = shouldRefreshRealCount ? domCount : storedRealCount;
      widget.dataset.cafJdgmRealReviewCount = String(realReviewCount);

      // Product-card review counts intentionally use the PDP seed names so card/PDP counts stay identical.
      // Aaron 2026-05-02: bumped boost 20-50 → 30-60, empty 25-75 → 40-100 per
      // "tăng lên xíu mà vẫn trust". Empty floor 40 reads as established product;
      // ceiling capped at 100 to stay under the psychological trust line —
      // anything past 100 starts to feel fake when the reviews page is empty.
      const syntheticReviewCount =
        realReviewCount > 0
          ? realReviewCount + seededIntegerInRange(`caf-pdp-review-count:${productId}`, 30, 60)
          : seededIntegerInRange(`caf-pdp-review-empty:${productId}`, 40, 100);

      if (realReviewCount === 0) {
        previewBadge.setAttribute('data-average-rating', '5.00');
        previewBadge.dataset.averageRating = '5.00';
        const starsNode = previewBadge.querySelector('.jdgm-prev-badge__stars');
        if (starsNode) {
          starsNode.setAttribute('data-score', '5.00');
          const stars = Array.from(starsNode.querySelectorAll('.jdgm-star'));
          if (stars.length === 0) {
            for (let index = 0; index < 5; index += 1) {
              const star = document.createElement('span');
              star.className = 'jdgm-star jdgm--on';
              starsNode.appendChild(star);
            }
          }
          starsNode.querySelectorAll('.jdgm-star').forEach((star) => {
            star.classList.remove('jdgm--off');
            star.classList.add('jdgm--on');
          });
        }
      }

      textNodes.forEach((node) => {
        if (!node.dataset.cafOriginalText) {
          node.dataset.cafOriginalText = node.textContent || '';
        }
        const rating = getSyntheticJudgeMeDisplayRating(productId);
        node.innerHTML = formatReviewCountText(
          syntheticReviewCount,
          node.dataset.cafOriginalText,
          rating,
        );
        node.setAttribute('aria-label', `${rating} out of 5 stars, ${formatIntegerWithCommas(syntheticReviewCount)} reviews`);
      });

      widget.dataset.cafJdgmDisplayRating = getSyntheticJudgeMeDisplayRating(productId);
      previewBadge.dataset.cafPreviewReviewFormat = 'rating-count';
      previewBadge.dataset.cafDisplayRating = getSyntheticJudgeMeDisplayRating(productId);
      previewBadge.dataset.cafSyntheticApplied = 'true';
      previewBadge.setAttribute('data-number-of-reviews', String(syntheticReviewCount));
      previewBadge.dataset.numberOfReviews = String(syntheticReviewCount);
      widget.dataset.cafJdgmSyntheticReviewCount = String(syntheticReviewCount);
    });
  };

  allowNativeContextMenu();
  guardBrokenJudgeMeReviewImages();

  window.CAFThemeHotfixShared = {
    CART_PREVIEW_CHECKOUT_SELECTOR,
    CART_PREVIEW_MUTATION_SELECTOR,
    DEFAULT_PDP_LAYOUT_MODE,
    DISCOUNT_PRICE_APP_SELECTOR,
    JDGM_PREVIEW_BADGE_SELECTOR,
    JUDGE_ME_REVIEW_WIDGET_SELECTORS,
    JUDGE_ME_REVIEW_IMAGE_GROUP_SELECTOR,
    JUDGE_ME_REVIEW_IMAGE_SELECTOR,
    JUDGE_ME_REVIEW_IMAGE_SHELL_SELECTOR,
    JUDGE_ME_REVIEW_WIDGET_SELECTOR,
    JUDGE_ME_LOAD_EVENTS,
    MOBILE_POPUP_BREAKPOINT,
    PDP_LAYOUT_STORAGE_KEY,
    POPUP_MODAL_SURFACE_SELECTOR,
    PRODUCT_DETAILS_SELECTOR,
    TEEINBLUE_EVENT_COMPONENT_INJECTED,
    applySyntheticJudgeMePreviewCounts,
    bindJudgeMeAndTeeinblueEvents,
    clearInlineContextMenuLocks,
    createRafThrottled,
    extractFirstInteger,
    formatReviewCountText,
    getJudgeMePreviewRatingValue,
    getSyntheticJudgeMeDisplayRating,
    hasBodyClassMatch,
    hasFacetedFilterUi,
    isCartContext,
    isCollectionOrSearchContext,
    isPathCart,
    isProductContext,
    mutationsContainAddedNode,
    mutationsHaveAddedNodes,
    nodeMatchesOrContains,
    normalizeInlineText,
    normalizePdpLayoutMode,
    pathIncludes,
    removeAll,
    removeClassFromAll,
    resolvePdpLayoutMode,
    runWithDelays,
    seededIntegerInRange,
    setImportantStyles,
    unwrapAll,
  };
})();
